/**
 * Spinner - Loading spinner component with Pod Ops styling
 */

import React from 'react'
import { theme } from '../../theme'
import { Loader2 } from 'lucide-react'

export interface SpinnerProps {
  size?: 'small' | 'default' | 'large'
  tip?: string
  spinning?: boolean
  children?: React.ReactNode
  style?: React.CSSProperties
  fullscreen?: boolean
}

export const Spinner: React.FC<SpinnerProps> = ({
  size = 'default',
  tip,
  spinning = true,
  children,
  style,
  fullscreen = false,
}) => {
  // Inject the keyframe animation once per document.
  // This must stay above the early returns below: hooks have to run in the same
  // order on every render, and `spinning` toggling would otherwise change how
  // many hooks this component calls.
  React.useEffect(() => {
    if (!document.getElementById('spinner-keyframes')) {
      const styleEl = document.createElement('style')
      styleEl.id = 'spinner-keyframes'
      styleEl.textContent = `
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `
      document.head.appendChild(styleEl)
    }
  }, [])

  const getSize = () => {
    switch (size) {
      case 'small':
        return 16
      case 'large':
        return 48
      default:
        return 24
    }
  }

  const iconSize = getSize()

  if (!spinning && !children) return null
  if (!spinning && children) return <>{children}</>

  // Styles
  const containerStyle: React.CSSProperties = {
    position: 'relative',
    width: '100%',
    height: '100%',
    ...style,
  }

  const overlayStyle: React.CSSProperties = {
    position: fullscreen ? 'fixed' : 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    zIndex: fullscreen ? theme.zIndex.modal : 1000,
    gap: theme.spacing.sm,
  }

  const spinnerStyle: React.CSSProperties = {
    animation: 'spin 1s linear infinite',
    color: theme.colors.primary,
  }

  const tipStyle: React.CSSProperties = {
    ...theme.typography.body,
    color: theme.colors.text.secondary,
  }

  const blurredContentStyle: React.CSSProperties = {
    filter: spinning ? 'blur(1px)' : 'none',
    pointerEvents: spinning ? 'none' : 'auto',
    userSelect: spinning ? 'none' : 'auto',
  }

  if (!children) {
    // Standalone spinner
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: theme.spacing.sm,
          ...style,
        }}
      >
        <Loader2 size={iconSize} style={spinnerStyle} />
        {tip && <div style={tipStyle}>{tip}</div>}
      </div>
    )
  }

  // Spinner with children (overlay mode)
  return (
    <div style={containerStyle}>
      <div style={blurredContentStyle}>{children}</div>
      {spinning && (
        <div style={overlayStyle}>
          <Loader2 size={iconSize} style={spinnerStyle} />
          {tip && <div style={tipStyle}>{tip}</div>}
        </div>
      )}
    </div>
  )
}

// Export Spin as alias for backwards compatibility
export const Spin = Spinner
